import { forwardRef, ReactNode } from "react";
import { SvgIcon, SvgIconProps } from "@mui/material";
import { CoreIconButton, CoreIconButtonProps } from "./Button";
import CaretUp from "../../images/caret-up.svg?react";
import CaretSmall from "../images/caret-small.svg?react";

export type CoreExpandButtonProps = Omit<CoreIconButtonProps, "tooltip" | "onClick" | "onChange" | "children"> & {
	expanded: boolean;
	onChange: (newExpanded: boolean) => void;
	collapsedTooltip?: ReactNode;
	expandedTooltip?: ReactNode;
	iconSize?: CoreExpandIconProps["size"];
	iconColor?: CoreExpandIconProps["color"];
	iconVariant?: CoreExpandIconProps["variant"];
	slotProps?: {
		icon?: Omit<SvgIconProps, "children" | "color">;
	};
} & Pick<CoreExpandIconProps, "defaultExpandedDirection" | "collapsedRight">;

const CoreExpandButton = forwardRef<HTMLButtonElement, CoreExpandButtonProps>(function CoreExpandButton(
	{
		variant,
		collapsedRight = false,
		expanded,
		onChange,
		collapsedTooltip,
		expandedTooltip,
		iconColor = "#4d4d4d",
		iconSize = 18,
		iconVariant,
		slotProps,
		defaultExpandedDirection,
		...buttonProps
	},
	ref,
) {
	return (
		<CoreIconButton
			{...({ variant, color: variant === "plain" ? "#4D4D4D" : undefined, ...buttonProps } as CoreIconButtonProps)}
			onClick={() => onChange(!expanded)}
			tooltip={expanded ? expandedTooltip : collapsedTooltip}
			aria-expanded={expanded}
			ref={(elem) => {
				if (elem) {
					if (typeof ref === "function") {
						ref(elem);
					} else if (ref) {
						ref.current = elem;
					}
				}
			}}
		>
			<CoreExpandIcon
				expanded={expanded}
				collapsedRight={collapsedRight}
				color={iconColor}
				size={iconSize}
				variant={iconVariant}
				defaultExpandedDirection={defaultExpandedDirection}
				{...slotProps?.icon}
			/>
		</CoreIconButton>
	);
});

type CoreExpandIconProps = {
	collapsedRight?: boolean;
	expanded: boolean;
	size?: number;
	color?: string;
	// There are 2 types of arrows now??
	variant?: "wide" | "narrow";
	defaultExpandedDirection?: "up" | "down";
} & Omit<SvgIconProps, "children" | "color">;

export function CoreExpandIcon({
	collapsedRight = false,
	expanded,
	size = 18,
	color = "#6f6f6f",
	variant = "wide",
	defaultExpandedDirection = "up",
	...props
}: CoreExpandIconProps) {
	const icon: ReactNode = variant === "wide" ? <CaretUp /> : <CaretSmall />;

	const expandedTransform = defaultExpandedDirection === "up" ? "rotate(0deg)" : "rotate(180deg)";
	const collapsedTransform = defaultExpandedDirection === "up" ? "rotate(180deg)" : "rotate(0deg)";

	return (
		<SvgIcon
			inheritViewBox={variant === "narrow"}
			{...props}
			sx={{
				fontSize: size,
				color,
				transform: expanded ? expandedTransform : collapsedRight ? "rotate(90deg)" : collapsedTransform,
				transformOrigin: "center",
				...props.sx,
			}}
		>
			{icon}
		</SvgIcon>
	);
}

export default CoreExpandButton;
